import { authScopeForCompanyClientData, toCustomAuthHeader } from '@upright/common/iam/access/custom-auth';
import {
  AddCompanyClientDataRequest,
  AddCompanyClientDataResponse,
  ChangeCompanyClientDatasetStateResponse,
  DeleteCompanyClientDataRequest,
  DeleteCompanyClientDataResponse,
  EditCompanyClientDataRequest,
  EditCompanyClientDataResponse,
  GetCompanyClientDatasetResponse,
} from '@upright/common/model/company-client-data/api-types';
import { CompanyClientDatasetState, CompanyClientDataType } from '@upright/common/model/company-client-data/types';
import { fetchOrThrow } from '../../common/fetchers/fetch-or-throw';
import { ApiTarget } from '../../common/types';

export async function fetchCompanyClientDataset<T extends CompanyClientDataType>(
  companyId: string,
  type: T,
  isDraft: boolean,
  clientSecret?: string,
) {
  return await fetchOrThrow<GetCompanyClientDatasetResponse<T>>(
    `/company-client-data/${companyId}/${type}/${isDraft ? 'TRUE' : 'FALSE'}`,
    'GET',
    undefined,
    ApiTarget.SERVER,
    getAuthHeader(clientSecret, companyId),
  );
}

export async function addClientData<T extends CompanyClientDataType>(
  datasetId: number | undefined,
  payload: AddCompanyClientDataRequest<T>,
  companyId: string,
  clientSecret: string | undefined,
): Promise<AddCompanyClientDataResponse<T>> {
  if (!datasetId) {
    throw Error(`Cannot add datum, missing dataset ID for company ${companyId}`);
  }
  return await fetchOrThrow(
    `/company-client-data/${datasetId}`,
    'POST',
    payload,
    ApiTarget.SERVER,
    getAuthHeader(clientSecret, companyId),
  );
}

export async function editClientData<T extends CompanyClientDataType>(
  datasetId: number,
  payload: EditCompanyClientDataRequest<T>,
  companyId: string,
  clientSecret: string | undefined,
): Promise<EditCompanyClientDataResponse> {
  return await fetchOrThrow(
    `/company-client-data/${datasetId}`,
    'PATCH',
    payload,
    ApiTarget.SERVER,
    getAuthHeader(clientSecret, companyId),
  );
}

export async function deleteClientData(
  datasetId: number,
  ids: DeleteCompanyClientDataRequest,
  companyId: string,
  clientSecret: string | undefined,
): Promise<DeleteCompanyClientDataResponse> {
  return await fetchOrThrow(
    `/company-client-data/${datasetId}`,
    'DELETE',
    ids,
    ApiTarget.SERVER,
    getAuthHeader(clientSecret, companyId),
  );
}

export async function changeCompanyClientDatasetState(
  companyId: string,
  datasetId: number,
  datasetState: CompanyClientDatasetState,
  clientSecret?: string,
): Promise<ChangeCompanyClientDatasetStateResponse> {
  return await fetchOrThrow(
    `/company-client-data/status/${datasetId}/${datasetState}`,
    'PATCH',
    undefined,
    ApiTarget.SERVER,
    getAuthHeader(clientSecret, companyId),
  );
}

function getAuthHeader(clientSecret: string | undefined, companyId: string) {
  return clientSecret ? toCustomAuthHeader(authScopeForCompanyClientData(companyId), clientSecret) : undefined;
}
